import React, { memo, useCallback, useMemo } from 'react';
import { View, } from 'react-native';
import Animated from 'react-native-reanimated';
import { rem, SCROLL_INDICATOR_INSETS, Separator } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import IconCheck from 'design-system/icons/IconCheck';
import useColors from 'hooks/useColors';
import Text from 'design-system/Text';
import { selectIsGBUser } from 'reducers/selectors';
import { selectPotProductWithTheHighestInterest } from 'features/pots/reducers/selectors';
import { selectHasAUMFeesFeature, selectHasFxFeesFeature, } from 'features/invest/reducers/selectors';
import { selectHasRentReporting } from 'features/rent-reporting/selectors';
import { formatRentReportingAgencies } from 'features/rent-reporting/utils';
import SubscriptionCard from './SubscriptionCard';
import { formatMonthlyPriceFromYearly } from '../trial/utils';
import { selectConnectionsLimit, selectFilteredBenefitsDataForTierWithCustomAndTopFeatures, selectRentReportingAgenciesTierMap, } from '../selectors';
import { Tier } from '../entities';
import { tierThemes } from '../benefits';
const keyExtractor = (item) => item.name;
const ACTIVE_PADDING_TOP = rem(110);
const INACTIVE_PADDING_TOP = rem(120);
const TierFlatListTrial = ({ scrollRef, isActive, tier, localizedPrice, yearlyPrice, currency, hasButton, contentContainerStyle, onScroll, showSelector = true, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const headerHeight = useDefaultHeaderHeight();
    const benefitsData = useAppSelector((store) => selectFilteredBenefitsDataForTierWithCustomAndTopFeatures(store, tier));
    const connectionsLimitMap = useAppSelector(selectConnectionsLimit);
    const isGbUser = useAppSelector(selectIsGBUser);
    const hasRentReportingFeature = useAppSelector(selectHasRentReporting);
    const hasFxFeesFeature = useAppSelector(selectHasFxFeesFeature);
    const hasAUMFeesFeature = useAppSelector(selectHasAUMFeesFeature);
    const tierFxRateMap = useAppSelector((store) => store.invest.tierFxRateMap);
    const tierAumFeesMap = useAppSelector((store) => store.invest.tierSecclAUMFeesMap);
    const rentReportingTierMap = useAppSelector(selectRentReportingAgenciesTierMap);
    const tierRateMap = useAppSelector((store) => store.pots.interestPotsTierRateMap);
    const maxInterestProduct = useAppSelector(selectPotProductWithTheHighestInterest);
    const listItemsForTier = useMemo(() => ({
        [Tier.ultimate]: [
            <>
          Everything in{' '}
          <Text TextThin-14 Primary>
            {tierThemes[Tier.pro].shortName}
          </Text>
        </>,
            ...(isGbUser ? ['Business accounts'] : []),
            'Create spaces to organise your cash with your life or business partners',
            ...(tierRateMap && isGbUser
                ? [
                    <>
                Earn{' '}
                <Text TextThin-14 Primary>
                  {tierRateMap?.[Tier.ultimate]}%
                </Text>{' '}
                AER paid{' '}
                {maxInterestProduct?.interestPaymentFrequency?.toLowerCase() ||
                            'daily'}
              </>,
                ]
                : []),
            ...(hasFxFeesFeature && tierFxRateMap?.ULTIMATE
                ? [`Reduced FX fee of ${tierFxRateMap.ULTIMATE}% when investing`]
                : []),
            ...(hasAUMFeesFeature && tierAumFeesMap?.ULTIMATE
                ? [`Reduced AUM fee of ${tierAumFeesMap.ULTIMATE}% when investing`]
                : []),
        ],
        [Tier.pro]: [
            <>
          Everything in{' '}
          <Text TextThin-14 Primary>
            {tierThemes[Tier.plus].shortName}
          </Text>
        </>,
            ...(rentReportingTierMap && hasRentReportingFeature
                ? [
                    `Credit building with ${formatRentReportingAgencies(rentReportingTierMap.PRO)}`,
                ]
                : []),
            'Unlimited bank connections & offline accounts',
            'Networth tracking to monitor your financial health over time',
            ...(tierRateMap && isGbUser
                ? [
                    <>
                Earn{' '}
                <Text TextThin-14 Primary>
                  {tierRateMap?.[Tier.pro]}%
                </Text>{' '}
                AER paid{' '}
                {maxInterestProduct?.interestPaymentFrequency?.toLowerCase() ||
                            'daily'}
              </>,
                ]
                : []),
            ...(hasFxFeesFeature && tierFxRateMap?.PRO
                ? [`Reduced FX fee of ${tierFxRateMap.PRO}% when investing`]
                : []),
            ...(hasAUMFeesFeature && tierAumFeesMap?.PRO
                ? [`Reduced AUM fee of ${tierAumFeesMap.PRO}% when investing`]
                : []),
        ],
        [Tier.plus]: [
            ...(rentReportingTierMap && hasRentReportingFeature
                ? [
                    `Credit building with ${formatRentReportingAgencies(rentReportingTierMap.PLUS)}`,
                ]
                : []),
            ...(connectionsLimitMap?.PLUS
                ? [`Up to ${connectionsLimitMap.PLUS} bank connections`]
                : []),
            'Cashback at more than 500 brands',
            ...(tierRateMap && isGbUser
                ? [
                    <>
                Earn{' '}
                <Text TextThin-14 Primary>
                  {tierRateMap?.[Tier.plus]}%
                </Text>{' '}
                AER paid{' '}
                {maxInterestProduct?.interestPaymentFrequency?.toLowerCase() ||
                            'daily'}
              </>,
                ]
                : []),
            ...(hasFxFeesFeature && tierFxRateMap?.PLUS
                ? [`Reduced FX fee of ${tierFxRateMap.PLUS}% when investing`]
                : []),
            ...(hasAUMFeesFeature && tierAumFeesMap?.PLUS
                ? [`Reduced AUM fee of ${tierAumFeesMap.PLUS}% when investing`]
                : []),
        ],
    }), [
        isGbUser,
        tierRateMap,
        tierFxRateMap,
        tierAumFeesMap,
        hasFxFeesFeature,
        hasAUMFeesFeature,
        rentReportingTierMap,
        hasRentReportingFeature,
        connectionsLimitMap.PLUS,
        maxInterestProduct?.interestPaymentFrequency,
    ]);
    let selectedTier = Tier.pro;
    if (tier === 'ULTIMATE')
        selectedTier = Tier.ultimate;
    if (tier === 'PLUS')
        selectedTier = Tier.plus;
    const renderPlanBenefitChecklist = useCallback((someTier) => (<View>
        <Separator style={styles.separator}/>
        {listItemsForTier[someTier].map((item, index) => (<View key={String(index)} style={styles.itemContainer}>
            <IconCheck thick color={colors.elements.brand} width={20} height={20}/>
            <Text TextThin-14 Secondary style={styles.text}>
              {item}
            </Text>
          </View>))}
      </View>), [
        styles.separator,
        styles.itemContainer,
        styles.text,
        listItemsForTier,
        colors.elements.brand,
    ]);
    const renderHeader = useCallback(() => isActive ? null : (<SubscriptionCard tier={tier} price={tier === Tier.free ? formatAmount(0, currency) : localizedPrice} yearlyPrice={yearlyPrice && currency
            ? formatMonthlyPriceFromYearly({
                price: yearlyPrice,
                currency,
            })
            : undefined} discount={selectedTier === Tier.pro ? 'BEST VALUE' : ''} showTrialWithChildren>
          {renderPlanBenefitChecklist(selectedTier)}
        </SubscriptionCard>), [
        currency,
        isActive,
        localizedPrice,
        tier,
        yearlyPrice,
        renderPlanBenefitChecklist,
        selectedTier,
    ]);
    const paddingBottom = useMarginBottom('paddingBottom', hasButton || !isActive ? HEIGHT + rem(16) : 0);
    return (<Animated.FlatList ref={scrollRef} data={benefitsData} renderItem={null} ListHeaderComponent={renderHeader} ListFooterComponent={null} scrollEventThrottle={16} initialNumToRender={2} keyExtractor={keyExtractor} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={onScroll} contentContainerStyle={[
            {
                paddingTop: headerHeight +
                    (isActive ? ACTIVE_PADDING_TOP : INACTIVE_PADDING_TOP) -
                    (showSelector ? 0 : rem(69)),
            },
            paddingBottom,
            contentContainerStyle,
        ]}/>);
};
const styleSet = createStyleSheets(() => ({
    itemContainer: {
        flexDirection: 'row',
        marginBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    text: {
        marginLeft: rem(8),
        flexShrink: 1,
    },
    separator: {
        marginBottom: rem(16),
    },
}));
export default memo(TierFlatListTrial);
